import type {
  BrandScheduler,
  ExecutionSummary,
  WorkflowExecutionStatus,
  WorkflowTaskStatus
} from './BrandWorkflows.types';

export type PillVariant = 'default' | 'info' | 'success' | 'warning' | 'danger';

/* "Jun 30, 2026 2:05PM" — the shared formatDate util pins EST, so it can't be reused here */
export const formatWorkflowTimestamp = (isoDate: string | Date | null): string => {
  if (!isoDate) return '—';
  const date = typeof isoDate === 'string' ? new Date(isoDate) : isoDate;
  if (Number.isNaN(date.getTime())) return '—';

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timePart = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(' ', '');
  return `${datePart} ${timePart}`;
};

/* "4m 32s" / "1h 2m" — dash until the run has both started and ended */
export const formatWorkflowDuration = (
  startedAt: string | null,
  endedAt: string | null
): string => {
  if (!startedAt || !endedAt) return '—';
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

/* A run's clock starts when its first task actually starts, not when the execution was marked
   in progress — that happens as soon as the first task is queued, so it includes the wait for a
   runner. Falls back for runs whose tasks never started. */
export const executionDurationStart = (
  execution: Pick<ExecutionSummary, 'firstTaskStartedAt' | 'startedAt'>
): string | null => execution.firstTaskStartedAt ?? execution.startedAt;

/* InProgress is yellow on a workflow's first ever run, blue on re-runs */
export const executionPillVariant = (
  status: WorkflowExecutionStatus,
  isFirstRun: boolean
): PillVariant => {
  switch (status) {
    case 'Success':
      return 'success';
    case 'Failure':
      return 'danger';
    case 'InProgress':
      return isFirstRun ? 'warning' : 'info';
    default:
      return 'default';
  }
};

export const taskPillVariant = (status: WorkflowTaskStatus): PillVariant => {
  switch (status) {
    case 'Success':
      return 'success';
    case 'Failure':
      return 'danger';
    case 'InProgress':
      return 'info';
    default:
      return 'default';
  }
};

/* UUIDs are too long to read in a row — show just enough to tell rows apart ("d79ae"); the
   full id stays reachable via copy + tooltip */
export const shortenExecutionId = (executionId: string): string => {
  if (!executionId) return '—';
  const [firstSegment] = executionId.split('-');
  return firstSegment.slice(0, 5);
};

export const s3ConsoleUrl = (s3Uri: string): string | null => {
  const match = /^s3:\/\/([^/]+)\/(.+)$/.exec(s3Uri);
  if (!match) return null;
  const [, bucket, key] = match;
  return `https://s3.console.aws.amazon.com/s3/object/${bucket}?prefix=${encodeURIComponent(key)}`;
};

/* Backend-provided URLs land in anchor hrefs; only https may pass through */
export const httpsUrl = (url: string | null): string | null =>
  url?.startsWith('https://') ? url : null;

/* Multiple schedulers per type are legal (scheduler configs); show the active one, else the first */
export const pickSchedulerByType = (schedulers: BrandScheduler[]): Map<string, BrandScheduler> => {
  const byType = new Map<string, BrandScheduler>();
  schedulers.forEach(scheduler => {
    const existing = byType.get(scheduler.workflowType);
    if (!existing || (!existing.isActive && scheduler.isActive)) {
      byType.set(scheduler.workflowType, scheduler);
    }
  });
  return byType;
};
